/*
 * @providesModule EmailCodeInput
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {_m} from 'INTL';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {getStyles} from 'SMSVerifyStyle';
import {formatCountdown, useCountdownNumber} from '@skillz/ui';
import OtpInputs from 'react-native-otp-inputs';
import SkillzStyle from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import {EmailFieldType} from 'SignInView';
import DynamicCheckbox from '../../DynamicCheckbox/DynamicCheckbox';
import {sendEmailOTP} from '../../../Utilities/EmailOTPUtils';
import BannerModal from 'BannerModal';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import CurrentUserStore from 'CurrentUserStore';
import {createStyles} from './EmailCodeInputStyle';

// Countdown Timer Component that can be remounted
const CountdownTimer = ({
  onComplete,
  onResend,
  styles,
}: {
  onComplete: () => void;
  onResend: () => void;
  styles: any;
}) => {
  const {t} = useTranslation();
  const [enableResend, setEnableResend] = useState(false);
  const SECOND_TO_MS = 1000;

  const resendTime = useCountdownNumber(30, () => {
    setEnableResend(true);
    onComplete();
  });

  const countdown = enableResend
    ? _m('Resend OTP')
    : _m('Resend in {0}', formatCountdown(t, resendTime * SECOND_TO_MS));

  return (
    <Text
      allowFontScaling={false}
      onPress={() => enableResend && onResend()}
      style={[styles.countdownText, !enableResend && styles.countdownTextDisabled]}
    >
      {countdown}
    </Text>
  );
};

export type EmailCodeInputProps = {
  time: number;
  //number of digits for code input
  numberOfInputs: number;
  // function to be called on edit press for user
  editPress: () => void;
  // flag to disable editing of phone number
  disableEdit?: boolean;
  email: string;
  errorStyle: {
    errorStyle: boolean;
    setErrorStyle: React.Dispatch<React.SetStateAction<boolean>>;
  };
  colorVariant?: 'dark';
  isNewUI?: boolean;
  fieldType?: EmailFieldType;
  // Callback when OTP code is completed
  onCodeComplete?: (code: string) => void;
  forgotPasswordCallback?: () => void;
  handlePassword?: (value: string) => void;
  gameTheme?: any;
  // Checkbox props
  showCheckbox?: boolean;
  checkboxText?: string;
  onCheckboxToggle?: (isChecked: boolean) => void;
  onOtpMethodSelected: () => Promise<boolean>;
  isSignUp?: boolean;
  // Callback for code validation to trigger exit deposit flow
  codeValidation?: () => void;
};
export const EmailCodeInput = ({
  time,
  numberOfInputs,
  editPress,
  disableEdit,
  email,
  errorStyle,
  colorVariant,
  fieldType,
  isNewUI,
  onCodeComplete,
  forgotPasswordCallback,
  handlePassword,
  gameTheme,
  // Checkbox props
  showCheckbox = false,
  checkboxText = _m('I agree to the terms and conditions'),
  onCheckboxToggle,
  onOtpMethodSelected,
  isSignUp = false,
  codeValidation,
}: EmailCodeInputProps) => {
  const styles = getStyles(colorVariant);

  const {viewSecondaryColor} = SkillzStyle.getOverriddenFallbackTheme();

  const [password, setPassword] = useState('');
  const {textColorPrimary, textColorSecondary, playButtonGradient} =
    SkillzStyle.getOverriddenFallbackTheme();

  const inputRef = useRef<any>(null);

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  const st = createStyles(gameTheme, isDarkTheme);

  const [successBannerVisible, setSuccessBannerVisible] = useState({visible: false, message: ''});

  // error banner after verify otp api call
  const [serverErrorBanner, setServerErrorBanner] = useState({visible: false, message: ''});

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // slight delay ensures keyboard opens reliably
    return () => clearTimeout(timer);
  }, []);

  const [countdownKey, setCountdownKey] = useState(0);

  const handleChange = (otpCode: string) => {
    // Only allow numeric characters and restrict length
    const numericOnly = otpCode.replace(/[^0-9]/g, '');
    const restrictedCode = numericOnly.slice(0, numberOfInputs);

    // If the processed code is different from input, return early
    if (restrictedCode !== otpCode) {
      return;
    }

    if (restrictedCode.length === numberOfInputs) {
      onCodeComplete?.(restrictedCode);
      codeValidation?.();
    } else {
      errorStyle?.setErrorStyle(false);
    }
  };

  const handleResendOTP = async () => {
    if (isSignUp) {
      trackTelemetryEvent({
        event: TelemetryEventName.SIGNIN_PAGE_RESEND_OTP_CLICKED,
        payload: {
          signin_type: 'signup',
        },
      });
    } else {
      trackTelemetryEvent({
        event: TelemetryEventName.SIGNIN_PAGE_RESEND_OTP_CLICKED,
        payload: {
          signin_type: 'login',
        },
      });
    }

    try {
      // Clear all previous banner states to prevent conflicts
      setSuccessBannerVisible({visible: false, message: ''});
      setServerErrorBanner({visible: false, message: ''});

      const result = await sendEmailOTP({
        email: email,
        isSignUp: isSignUp,
        displayName: '',
      });

      if (result.success) {
        // Reset the countdown timer by incrementing the key to force remount
        setCountdownKey((prev) => prev + 1);
        setSuccessBannerVisible({
          visible: true,
          message: result?.message || _m('OTP sent successfully'),
        });
      } else {
        setServerErrorBanner({visible: true, message: result?.message || _m('Failed to send OTP')});
      }
    } catch (error) {
      console.warn('Failed to resend OTP:', error);
    }
  };

  const renderErrorBanner = useMemo(() => {
    if (successBannerVisible.visible) {
      return (
        <View style={st.bannerContainer}>
          <BannerModal
            title={_m('Success')}
            message={_m(successBannerVisible.message)}
            numberOfLines={2}
            error={false}
            heightContainer={90}
            onClosePress={() => setSuccessBannerVisible({visible: false, message: ''})}
          />
        </View>
      );
    } else if (serverErrorBanner.visible) {
      return (
        <View style={st.bannerContainer}>
          <BannerModal
            title={_m('Error')}
            message={_m(serverErrorBanner.message)}
            numberOfLines={2}
            error={true}
            heightContainer={90}
            onClosePress={() => setServerErrorBanner({visible: false, message: ''})}
          />
        </View>
      );
    }

    return null;
  }, [successBannerVisible, serverErrorBanner]);

  const [textEntry, setTextEntry] = useState<boolean>(false);

  const onChangeTextEntry = () => {
    setTextEntry(!textEntry);
  };

  const [isSmsChecked, setIsSmsChecked] = useState(false);

  const handleSmsToggle = (isChecked: boolean) => {
    setIsSmsChecked(isChecked);
    CurrentUserStore.updateUserProperty('optInStatus', isSmsChecked.toString());
  };

  // Memoize placeholder color
  const placeholderTextColor = isDarkTheme ? textColorPrimary.color : textColorSecondary.color;
  const checkboxColor = isDarkTheme ? 'white' : viewSecondaryColor.backgroundColor;

  if (isNewUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={st.keyboardAvoidingContainer}
      >
        {renderErrorBanner}
        <ScrollView>
          <View>
            {fieldType === EmailFieldType.OTP ? (
              <Text allowFontScaling={false} style={st.newMessageHeader}>
                {_m('Enter {0}-Digit Code', numberOfInputs)}
              </Text>
            ) : (
              <Text allowFontScaling={false} style={st.newMessageHeader}>
                {_m('Enter your account password')}
              </Text>
            )}

            <View style={st.emailVerificationContainer}>
              {fieldType === EmailFieldType.OTP && (
                <Text allowFontScaling={false} style={st.verificationMessageText}>
                  {_m('Sent to')}{' '}
                  <Text
                    allowFontScaling={false}
                    onPress={disableEdit ? () => {} : editPress}
                    style={st.emailAddressText}
                  >
                    {email}
                    <SkillzSVG
                      tintColor={textColorPrimary.color}
                      fill='transparent'
                      source='edit_pencil.svg'
                      style={st.editIcon}
                    />
                  </Text>
                </Text>
              )}
            </View>

            {errorStyle?.errorStyle ? (
              <View>
                <OtpInputs
                  // @ts-ignore
                  ref={inputRef}
                  colorVariant={colorVariant}
                  numberOfInputs={numberOfInputs}
                  handleChange={handleChange}
                  autofillFromClipboard={true}
                  autofillListenerIntervalMS={2000}
                  style={isNewUI ? styles.newOtpStyle : styles.otpStyle}
                  filledStyles={styles.otpError}
                  focusStyles={styles.otpError}
                  inputStyles={st.otpInput}
                  inputContainerStyles={isNewUI ? st.otpContainerWithBorder : styles.otpContainer}
                  errorStyle={errorStyle?.errorStyle}
                  allowFontScaling={false}
                  inputMode='numeric'
                  // maxLength={1}
                />
              </View>
            ) : (
              <View>
                {/* Password Input - Always render but hide when not needed */}
                <View style={fieldType === EmailFieldType.PASSWORD ? st.visible : st.hidden}>
                  <View style={st.passwordInput}>
                    <TextInput
                      ref={fieldType === EmailFieldType.PASSWORD ? inputRef : undefined}
                      placeholder={_m('Enter password')}
                      value={password}
                      onChangeText={(value) => {
                        setPassword(value);
                        handlePassword?.(value);
                      }}
                      autoCorrect={false}
                      textContentType='password'
                      placeholderTextColor={placeholderTextColor}
                      style={[
                        st.passwordTextInput,
                        isDarkTheme ? st.passwordTextInputDark : st.passwordTextInputLight,
                      ]}
                      secureTextEntry={!textEntry}
                      keyboardType={'default'}
                      autoCapitalize={'none'}
                      allowFontScaling={false}
                      cursorColor={playButtonGradient[1]}
                    />
                    <TouchableOpacity onPress={onChangeTextEntry}>
                      <SkillzSVG
                        source={textEntry ? 'password_eye.svg' : 'eye-off.svg'}
                        style={st.icon}
                      />
                    </TouchableOpacity>
                  </View>
                  <Text
                    allowFontScaling={false}
                    onPress={forgotPasswordCallback}
                    style={st.forgotPasswordTextWithColor}
                  >
                    {_m('Forgot password?')}
                  </Text>
                </View>

                {/* OTP Input - Always render but hide when not needed */}
                <View style={fieldType === EmailFieldType.OTP ? st.visible : st.hidden}>
                  <OtpInputs
                    // @ts-ignore
                    ref={fieldType === EmailFieldType.OTP ? inputRef : undefined}
                    autoFocus={fieldType === EmailFieldType.OTP}
                    numberOfInputs={numberOfInputs}
                    handleChange={handleChange}
                    autofillFromClipboard={true}
                    autofillListenerIntervalMS={2000}
                    style={isNewUI ? styles.newOtpStyle : styles.otpStyle}
                    filledStyles={st.otpSelectedWithBorder}
                    focusStyles={st.otpSelected}
                    inputStyles={st.otpInput}
                    inputContainerStyles={[
                      isNewUI ? st.otpContainerWithBorder : styles.otpContainer,
                    ]}
                    allowFontScaling={false}
                    cursorColor={playButtonGradient[1]}
                    inputMode='numeric'
                    // maxLength={1}
                  />
                  <CountdownTimer
                    key={`countdown-${email}-${countdownKey}`}
                    onComplete={() => {}}
                    onResend={handleResendOTP}
                    styles={st}
                  />
                </View>
              </View>
            )}
          </View>

          {/* code for opt in check box */}
          {fieldType === EmailFieldType.OTP && showCheckbox && (
            <DynamicCheckbox
              text={checkboxText}
              onToggle={() => handleSmsToggle(!isSmsChecked)}
              accessibilityLabel='opt in checkbox'
              testID='email-code-input-checkbox'
              checkedColor={checkboxColor}
            />
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // Return null for non-newUI case for now
  return null;
};
